import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiMail, FiLock, FiUser, FiEye, FiEyeOff, FiCode, FiStar, FiCheck } from 'react-icons/fi';
import { toast } from 'react-toastify';
import axios from 'axios';

function Register() {
  const { accountType: urlAccountType } = useParams();
  const navigate = useNavigate();

  // Check if user is already logged in
  const isLoggedIn = localStorage.getItem('token');
  const existingRole = localStorage.getItem('userRole');

  // Determine initial state based on URL
  const getInitialAccountType = () => {
    if (urlAccountType === 'user') return 'user';
    if (urlAccountType === 'dev') return 'developer';
    return null;
  };

  const [accountType, setAccountType] = useState(getInitialAccountType());
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Redirect logged-in users
  useEffect(() => {
    if (isLoggedIn) {
      // Developer trying to register again - go to dashboard
      if (existingRole === 'developer') {
        navigate('/dashboard');
      }
      // User trying to register as developer - for now redirect to dashboard
      // In future could implement an "upgrade" feature
      else if (urlAccountType === 'dev' && existingRole === 'user') {
        toast.info('To upgrade to developer, please contact support or create a new account.');
        navigate('/dashboard');
      }
      // User trying to register as user again
      else if (existingRole === 'user') {
        navigate('/dashboard');
      }
    }
  }, [isLoggedIn, existingRole, urlAccountType, navigate]);

  // Update state when URL changes
  useEffect(() => {
    if (urlAccountType === 'user') {
      setAccountType('user');
    } else if (urlAccountType === 'dev') {
      setAccountType('developer');
    } else if (!urlAccountType) {
      setAccountType(null);
    }
  }, [urlAccountType]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const selectAccountType = (type) => {
    const urlType = type === 'developer' ? 'dev' : 'user';
    navigate(`/register/${urlType}`);
  };

  const goBack = () => {
    navigate('/register');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('https://extension.avalw.com/api/auth/register', {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        accountType: accountType
      });

      if (response.data.requiresVerification) {
        toast.success('Account created! Please check your email to verify your account.');
        navigate('/login', { state: { message: 'Please verify your email before logging in.' } });
      } else if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('userEmail', formData.email);
        localStorage.setItem('userRole', response.data.user?.role || 'user');
        localStorage.setItem('userId', response.data.user?.id);
        navigate('/dashboard');
      } else {
        navigate('/login');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Account Type Selection (when no URL param)
  if (!accountType) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white py-12 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl w-full"
        >
          <div className="text-center mb-10">
            <h1 className="text-4xl font-bold mb-3 text-gray-900">Create Your Account</h1>
            <p className="text-gray-600 text-lg">Choose how you want to use AVALW Extensions</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* User Account */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => selectAccountType('user')}
              className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-gray-900 cursor-pointer transition-all group"
            >
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-gray-900 transition-colors">
                <FiStar className="text-3xl text-gray-600 group-hover:text-white transition-colors" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">User</h2>
              <p className="text-gray-500 mb-6">For browsing and reviewing extensions</p>

              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-gray-600">
                  <FiCheck className="text-green-500 flex-shrink-0" />
                  <span>Browse & install extensions</span>
                </li>
                <li className="flex items-center gap-3 text-gray-600">
                  <FiCheck className="text-green-500 flex-shrink-0" />
                  <span>Write reviews & ratings</span>
                </li>
                <li className="flex items-center gap-3 text-gray-600">
                  <FiCheck className="text-green-500 flex-shrink-0" />
                  <span>Create collections</span>
                </li>
                <li className="flex items-center gap-3 text-gray-600">
                  <FiCheck className="text-green-500 flex-shrink-0" />
                  <span>Report issues</span>
                </li>
              </ul>

              <div className="mt-8 pt-6 border-t border-gray-100">
                <span className="inline-flex items-center gap-2 text-gray-900 font-medium group-hover:underline">
                  Continue as User
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </span>
              </div>
            </motion.div>

            {/* Developer Account */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => selectAccountType('developer')}
              className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-gray-900 cursor-pointer transition-all group relative overflow-hidden"
            >
              <div className="absolute top-4 right-4 bg-gray-900 text-white text-xs font-medium px-3 py-1 rounded-full">
                Recommended
              </div>

              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-gray-900 transition-colors">
                <FiCode className="text-3xl text-gray-600 group-hover:text-white transition-colors" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Developer</h2>
              <p className="text-gray-500 mb-6">For publishing extensions</p>

              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-gray-600">
                  <FiCheck className="text-green-500 flex-shrink-0" />
                  <span>Everything in User, plus:</span>
                </li>
                <li className="flex items-center gap-3 text-gray-600">
                  <FiCheck className="text-green-500 flex-shrink-0" />
                  <span>Publish extensions</span>
                </li>
                <li className="flex items-center gap-3 text-gray-600">
                  <FiCheck className="text-green-500 flex-shrink-0" />
                  <span>Developer dashboard & analytics</span>
                </li>
                <li className="flex items-center gap-3 text-gray-600">
                  <FiCheck className="text-green-500 flex-shrink-0" />
                  <span>Extension Builder tool</span>
                </li>
              </ul>

              <div className="mt-8 pt-6 border-t border-gray-100">
                <span className="inline-flex items-center gap-2 text-gray-900 font-medium group-hover:underline">
                  Continue as Developer
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </span>
              </div>
            </motion.div>
          </div>

          <div className="mt-8 text-center text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-gray-900 hover:underline font-medium">
              Log in
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  // Step 2: Registration Form
  return (
    <div className="min-h-screen flex items-center justify-center bg-white py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <button
          onClick={goBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <span>←</span> Back to account type
        </button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full mb-4">
            {accountType === 'developer' ? (
              <><FiCode className="text-gray-900" /> Developer Account</>
            ) : (
              <><FiStar className="text-gray-900" /> User Account</>
            )}
          </div>
          <h1 className="text-4xl font-bold mb-2 text-gray-900">Create Account</h1>
          <p className="text-gray-600">
            {accountType === 'developer'
              ? 'Start publishing extensions for Cronos Browser'
              : 'Discover and review amazing extensions'}
          </p>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-200">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-900">Username</label>
              <div className="relative">
                <FiUser className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className="w-full pl-12 pr-4 py-3 bg-white border-2 border-gray-200 rounded-lg focus:outline-none focus:border-gray-900 transition-all text-gray-900"
                  placeholder="johndoe"
                  required
                  minLength={3}
                  maxLength={50}
                  pattern="[a-zA-Z0-9]+"
                  title="Username can only contain letters and numbers"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-900">Email</label>
              <div className="relative">
                <FiMail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-12 pr-4 py-3 bg-white border-2 border-gray-200 rounded-lg focus:outline-none focus:border-gray-900 transition-all text-gray-900"
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-900">Password</label>
              <div className="relative">
                <FiLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full pl-12 pr-12 py-3 bg-white border-2 border-gray-200 rounded-lg focus:outline-none focus:border-gray-900 transition-all text-gray-900"
                  placeholder="••••••••"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-900"
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-900">Confirm Password</label>
              <div className="relative">
                <FiLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full pl-12 pr-4 py-3 bg-white border-2 border-gray-200 rounded-lg focus:outline-none focus:border-gray-900 transition-all text-gray-900"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-all disabled:opacity-50"
            >
              {loading ? 'Creating account...' : `Create ${accountType === 'developer' ? 'Developer' : 'User'} Account`}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-gray-500">
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="underline">Terms of Service</Link> and{' '}
            <Link to="/privacy" className="underline">Privacy Policy</Link>
          </p>
        </div>

        <div className="mt-6 text-center text-sm text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="text-gray-900 hover:underline font-medium">
            Log in
          </Link>
        </div>
      </motion.div>
    </div>
  );
}

export default Register;
